import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

export type OnboardingStatus = 'pending' | 'approved' | 'completed' | 'rejected';

export interface OnboardingRoleContact {
  name?: string;
  email?: string;
  phone?: string;
}

export interface OnboardingCreditRef {
  company?: string;
  contact?: string;
  phone?: string;
  email?: string;
}

export interface OnboardingRequestRow {
  id: string;
  business_name: string;
  contact_name: string | null;
  email: string;
  phone: string | null;
  billing_address: string | null;
  delivery_address: string | null;
  business_type: string | null;
  /** Set only when this application came in through a staff-sent invite. */
  business_category: 'chain' | 'franchisee' | 'independent' | null;
  est_volume: string | null;
  notes: string | null;
  accounting_contact: string | null;
  accounting_email: string | null;
  duns_number: string | null;
  tax_id: string | null;
  tax_id_doc_path: string | null;
  resale_cert_doc_path: string | null;
  /** CA seller's / resale permit number the applicant typed (1.170). */
  resale_permit_number: string | null;
  /** Applicant ticked "I understand sales tax will be charged" (no permit) — 1.171. */
  sales_tax_acknowledged_at: string | null;
  signature_path: string | null;
  /** Structured addresses (separate street/unit/city/state/zip), when present. */
  billing_address_json: Record<string, string> | null;
  delivery_address_json: Record<string, string> | null;
  /** ALL delivery locations from the application (multi-location form). */
  delivery_addresses: Array<{
    label?: string;
    line1?: string;
    unit?: string;
    city?: string;
    state?: string;
    zip?: string;
    formatted?: string;
    verified?: boolean;
  }> | null;
  /** What the APPLICANT typed, per field, before staff corrected it. */
  original_values: Record<string, unknown> | null;
  edited_at: string | null;
  edited_by: string | null;
  /** Short-lived signed URLs attached by admin-list-onboarding when docs exist. */
  tax_id_doc_url: string | null;
  resale_cert_doc_url: string | null;
  signature_url: string | null;
  delivery_windows: string | null;
  special_instructions: string | null;
  contacts: Record<string, OnboardingRoleContact> | null;
  credit_requested: boolean;
  credit_terms: string | null;
  credit_app: {
    entity_type?: string;
    years_in_business?: string;
    bank_name?: string;
    references?: OnboardingCreditRef[];
  } | null;
  terms_accepted_at: string | null;
  terms_version: string | null;
  authorized_by: string | null;
  status: OnboardingStatus;
  sf_customer_id: string | null;
  /** SF job id of the new-customer setup ticket (closed by ops with a $0 invoice). */
  sf_setup_job_id: string | null;
  created_customer_id: string | null;
  reject_reason: string | null;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
  /** Only meaningful on approved rows — set by admin-list-onboarding. */
  qbo_ready: boolean;
  qbo_customer_id: number | null;
  /**
   * Chain application (filed through a chain's /apply/:token link). NULL on a
   * direct /apply submission — which is also how the queue tells the two
   * apart: a chain store carries no terms, credit or tax documents because
   * its master account settles all of that.
   */
  link_token: string | null;
  group_master_customer_id: string | null;
  /** The chain master's name, hydrated by admin-list-onboarding. */
  chain_master_name: string | null;
  store_code: string | null;
  store_phone: string | null;
  /** Franchise equipment ask — [{category,label,qty,notes?}] or null. */
  equipment_interest: Array<{ category: string; label: string; qty: number; notes?: string }> | null;
  opens_on: string | null;
  preferred_delivery_days: string[] | null;
}

export function useOnboardingRequests(status: OnboardingStatus | 'all') {
  return useQuery<{ ok: true; count: number; rows: OnboardingRequestRow[] }>({
    queryKey: ['admin', 'onboarding', status],
    queryFn: () =>
      adminFetch(
        status === 'all'
          ? 'admin-list-onboarding'
          : `admin-list-onboarding?status=${status}`,
      ),
    staleTime: 15 * 1000,
  });
}

export interface DedupeMatch {
  qbo_customer_id: number;
  display_name: string;
  email: string | null;
}

export interface DecideResult {
  ok: true;
  status: string;
  sf_customer_id?: string | null;
  /** Non-fatal Service Fusion caveats from approval (e.g. custom fields were
   * rejected because they aren't defined in SF settings yet). */
  sf_warnings?: string[];
  sf_setup_job_id?: string | null;
  setup_job_error?: string | null;
  /** Set when the prospect already exists in QBO — approval is held pending. */
  duplicate?: boolean;
  matches?: DedupeMatch[];
  /** Set when approving via the "link to existing" path. */
  linked?: boolean;
  qbo_customer_id?: number;
}

export function useDecideOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      request_id: string;
      action: 'approve' | 'reject';
      reject_reason?: string;
      /** Approve: create a new SF customer even if dedupe found matches. */
      force_create?: boolean;
      /** Approve: link to this existing QBO customer instead of creating one. */
      link_qbo_customer_id?: number;
    }) =>
      adminFetch<DecideResult>('admin-decide-onboarding', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => {
      // A held-pending duplicate response didn't change anything — don't churn
      // the queue. Any real transition (approved/rejected) refreshes it.
      if (!data.duplicate) qc.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
    },
  });
}

export function useFinishOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { request_id: string; qbo_customer_id?: number }) =>
      adminFetch<{
        ok: true;
        ready: boolean;
        message?: string;
        customer_id?: string;
        user_id?: string;
        welcome_sent?: boolean;
        /** Contacts filed from the application (see application-to-customer). */
        contacts_written?: number;
        /**
         * Anything finish could not do for itself — a store-number clash, an
         * address it could not write, a step of the application apply that
         * failed. Named for the chain flow it started as; it now also carries
         * notes from a direct /apply.
         */
        chain_notes?: string[];
        opens_on?: string;
      }>('admin-finish-onboarding', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => {
      // Only refresh the queue when something actually changed (ready=true).
      if (data.ready) qc.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
    },
  });
}

/**
 * Re-copy an accepted application's details (contacts, accounting email, site
 * phone, delivery instructions, tax paperwork, the archived application PDF)
 * onto its customer. Finish does this automatically now; this is the path for
 * applications completed before it did, and the retry when a step failed.
 */
export function useApplyApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { request_id: string }) =>
      adminFetch<{
        ok: true;
        customer_id: string;
        contacts_written: number;
        notes: string[];
      }>('admin-apply-application', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
      // The customer's own surfaces (contacts, documents) change too.
      qc.invalidateQueries({ queryKey: ['admin', 'customer-contacts'] });
      qc.invalidateQueries({ queryKey: ['admin', 'customer-documents'] });
    },
  });
}

/** The fields staff may correct on a PENDING application. */
export type OnboardingEditableField =
  | 'business_name'
  | 'contact_name'
  | 'email'
  | 'phone'
  | 'store_code'
  | 'store_phone'
  | 'accounting_contact'
  | 'accounting_email'
  | 'duns_number'
  | 'business_type'
  | 'est_volume'
  | 'credit_terms'
  | 'delivery_windows'
  | 'special_instructions'
  | 'notes';

/** An address as the edit panel sends it — server stamps it source:'staff'. */
export interface OnboardingEditAddress {
  label?: string | null;
  line1: string;
  unit?: string | null;
  city: string;
  state: string;
  zip?: string | null;
}

/** The structured parts of the form, edited whole. */
export interface OnboardingStructuredEdits {
  contacts?: Record<string, OnboardingRoleContact> | null;
  billing_address_json?: OnboardingEditAddress | null;
  delivery_addresses?: OnboardingEditAddress[] | null;
}

/** Tax paperwork — editable while pending OR approved, until Finish (1.170). */
export type OnboardingTaxField = 'tax_id' | 'resale_permit_number';

/**
 * Correct a pending application before approving it — chiefly the business
 * name, which is used verbatim as the Service Fusion customer_name and
 * therefore names the QuickBooks customer forever.
 *
 * Corrections annotate rather than erase: the applicant's own value is kept
 * in `original_values` and rendered as "Applicant entered: …".
 */
export function useUpdateOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      request_id: string;
      fields?: Partial<Record<OnboardingEditableField | OnboardingTaxField, string | null>> &
        OnboardingStructuredEdits;
      /** A tax ID / resale certificate the applicant sent separately. */
      doc?: { kind: 'taxid' | 'resale'; name: string; type: string; data: string };
    }) =>
      adminFetch<{ ok: true; changed: string[]; note?: string }>('admin-update-onboarding', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
    },
  });
}
